import React from 'react';
import { StarIcon, BadgeCheckIcon, FlagIcon } from 'lucide-react';
import { reviews, reviewStats, reviewSummary, ratingBreakdown, Review } from '../data/product';
import { CtaButton } from './CtaButton';
import { ReviewFeed } from './ReviewFeed';
import { WriteReview } from './WriteReview';
const ROW_COUNT = 8;

/** Splits every review across the rows so no name or face is ever shown twice. */
function splitIntoRows(items: Review[], rowCount: number): Review[][] {
  const rows: Review[][] = Array.from({
    length: rowCount
  }, () => []);
  items.forEach((item, index) => {
    rows[index % rowCount].push(item);
  });
  return rows;
}
function ReviewCard({
  review
}: {
  review: Review;
}) {
  return <article className="flex h-full w-[300px] shrink-0 flex-col border border-white/12 bg-ink-950 p-5 sm:w-[350px]" data-id="element-785" data-mp="2i02zcf8q98g8">
      <span className="flex items-center gap-1" aria-label="Rated 5 out of 5" data-id="element-786" data-mp="28f5wtsx9y26">
        {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="h-4 w-4 fill-grit-500 text-grit-500" aria-hidden="true" data-id="element-787" data-mp="3tf9hhll96kxi" />)}
      </span>
      <blockquote className="mt-3 text-[15px] leading-relaxed text-steel-200" data-id="element-788" data-mp="yhd45g3gzgkg">“{review.quote}”</blockquote>
      <div className="mt-auto flex items-center gap-3 border-t border-white/10 pt-4" data-id="element-789" data-mp="13g6jzf68bjns">
        <img src={review.avatar} alt="" aria-hidden="true" className="h-14 w-14 shrink-0 object-cover object-[50%_35%]" data-id="element-790" data-mp="18bc4qanwasaa" />
        <span className="min-w-0" data-id="element-791" data-mp="fq2h5a0v7nxu">
          <span className="flex items-center gap-1.5 font-display text-lg font-bold uppercase leading-none text-offwhite" data-id="element-792" data-mp="fmoh48fobqtk">
            {review.name}
            <BadgeCheckIcon className="h-4 w-4 text-grit-500" aria-hidden="true" data-id="element-793" data-mp="op7i0mzwftd6" />
          </span>
          <span className="mt-1 block truncate text-[11px] font-semibold uppercase tracking-wide text-steel-500" data-id="element-794" data-mp="tgfslmcytkrx">
            {review.trade}
          </span>
          <span className="mt-0.5 block text-[11px] font-bold uppercase tracking-wide text-grit-500" data-id="element-795" data-mp="3t1upxnjnb1kl">
            {review.bought}
          </span>
        </span>
      </div>
    </article>;
}
export function SocialProofSection() {
  const rows = splitIntoRows(reviews, ROW_COUNT);
  return <section id="reviews" className="border-b border-white/10 bg-ink-900" data-id="element-796" data-mp="z9v49gcul58a">
      <div className="mx-auto max-w-[1400px] px-4 pt-14 sm:px-6 lg:px-8 lg:pt-20" data-id="element-797" data-mp="1e02a8wlo4vs3">
        <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-grit-500" data-id="element-798" data-mp="192ttn0g64zsy">
          <FlagIcon className="h-4 w-4" aria-hidden="true" data-id="element-799" data-mp="lcg8ltw0417v" />
          50,000+ shipped across America
        </p>
        <div className="mt-4 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between" data-id="element-800" data-mp="gexza2e2njzc">
          <h2 className="grit-headline max-w-2xl text-[clamp(2.5rem,9vw,5rem)] font-extrabold uppercase text-offwhite" data-id="element-801" data-mp="udxp3ddbm4x3">
            We&apos;re changing how
            <br data-id="element-802" data-mp="25r03c1ry6ie3" />
            <span className="text-grit-500" data-id="element-803" data-mp="25qgjugmnflxy">America works.</span>
          </h2>
          <ul className="grid grid-cols-2 gap-6 sm:grid-cols-4 lg:gap-10" data-id="element-804" data-mp="21qxzblyqpcvn">
            {reviewStats.map(stat => <li key={stat.label} data-id="element-805" data-mp="298w8tqgx8nyv">
                <span className="block font-display text-4xl font-extrabold leading-none text-grit-500" data-id="element-806" data-mp="29c1xx5pjvpul">
                  {stat.value}
                </span>
                <span className="mt-1 block text-[11px] font-bold uppercase tracking-[0.16em] text-steel-400" data-id="element-807" data-mp="1zct5wie8wlav">
                  {stat.label}
                </span>
              </li>)}
          </ul>
        </div>

        <div className="mt-10 grid gap-8 border border-white/12 bg-ink-950 p-6 lg:grid-cols-[auto_1fr] lg:items-center lg:gap-14 lg:p-8" data-id="element-808" data-mp="2s0yp30pni28l">
          <div data-id="element-809" data-mp="2ianewwfdiwt9">
            <p className="font-display text-7xl font-extrabold leading-none text-offwhite" data-id="element-810" data-mp="2ib5g87gw8181">
              {reviewSummary.rating}
              <span className="text-3xl text-steel-500" data-id="element-811" data-mp="2n7vwausp6wtg">/5</span>
            </p>
            <span className="mt-2 flex items-center gap-1" aria-label="Average rating 4.9 out of 5" data-id="element-812" data-mp="3axzw0r2rmcf6">
              {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="h-5 w-5 fill-grit-500 text-grit-500" aria-hidden="true" data-id="element-813" data-mp="3frm4s85t5w5p" />)}
            </span>
            <p className="mt-2 text-sm font-semibold uppercase tracking-wide text-steel-400" data-id="element-814" data-mp="1n23h5t3f4cda">
              {reviewSummary.reviewCount} verified reviews
            </p>
          </div>

          <ul className="grid gap-2" data-id="element-815" data-mp="1d4p3204yvzo5">
            {ratingBreakdown.map(row => <li key={row.stars} className="flex items-center gap-3" data-id="element-816" data-mp="2arxs2546fquw">
                <span className="w-12 shrink-0 text-xs font-bold uppercase tracking-wide text-steel-400" data-id="element-817" data-mp="2au0igq98rm05">
                  {row.stars} star
                </span>
                <span className="h-2.5 flex-1 bg-ink-800" data-id="element-818" data-mp="2112wga51jews">
                  <span className="block h-full bg-grit-500" style={{
                width: `${row.percent}%`
              }} data-id="element-819" data-mp="1w31c7lvvp9jq" />
                </span>
                <span className="w-10 shrink-0 text-right text-xs font-bold tabular-nums text-steel-300" data-id="element-820" data-mp="2tiynwka036a8">
                  {row.percent}%
                </span>
              </li>)}
          </ul>
        </div>
      </div>

      <div className="grit-marquee mt-10 space-y-5 overflow-hidden py-1" data-id="element-821" data-mp="2x1xjieoh3a9v">
        {rows.map((row, rowIndex) => <div key={`row-${rowIndex}`} className={`grit-fade-x overflow-hidden ${rowIndex > 2 ? 'hidden lg:block' : ''}`} data-id="element-822" data-mp="3140xb9ym6qyb" data-mpi={rowIndex}>
            <ul className={`flex w-max gap-5 ${rowIndex % 2 === 0 ? 'grit-track-left' : 'grit-track-right'}`} style={{
          animationDuration: `${58 + rowIndex * 7}s`
        }} data-id="element-823" data-mp="3b15m7hjuh0it">
              {[...row, ...row].map((review, index) => <li key={`${rowIndex}-${review.name}-${index}`} className="flex" data-id="element-824" data-mp="3oriwvro4etbu" data-mpi={index}>
                  <ReviewCard review={review} data-id="element-825" data-mp="3otv9rqf0udi1" />
                </li>)}
            </ul>
          </div>)}
      </div>

      <div className="mx-auto max-w-[1400px] px-4 pb-14 sm:px-6 lg:px-8 lg:pb-20" data-id="element-826" data-mp="2l3rn7hnmdoz">
        <ReviewFeed data-id="element-827" data-mp="7j5l8mcphvep" />
        <WriteReview data-id="element-828" data-mp="7im06wrreqwg" />

        <div className="mt-10 flex flex-col items-start gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-829" data-mp="7jx5j10us6po">
          <p className="text-[15px] text-steel-400" data-id="element-830" data-mp="391kv744z5evu">
            A small slice of {reviewSummary.reviewCount} verified reviews, and counting. Every X1 ships with all 3
            lenses and lifetime free replacement.
          </p>
          <CtaButton className="w-full sm:w-auto" data-id="element-831" data-mp="3izh4rri9k1ru">Join 50,000+ and get your X1</CtaButton>
        </div>
      </div>
    </section>;
}